import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { CartSummaryMap } from '@/components/modules/_shared/CartSummary/maps/CartSummary.map';
import { DefaultCartItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/constants/DefaultCartItem';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { KtuinProductType } from '@/components/modules/Application/components/Cart/KtuinCart/types/KtuinProductType';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

interface KtuinCartProps {
  type: KtuinProductType;
  price: string;
  description?: string;
  resetDescription?: () => void;
}

export const useKtuinCart = ({ type, price, description, resetDescription }: KtuinCartProps) => {
  const {
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const { cart, setCartItem, submitCart } = useCart();
  const { arePaymentMethodsAvailable } = usePaymentMethods();
  const {
    formState: { isValid },
  } = useFormContext();

  const cartItem = useMemo(() => {
    if (!cart || cart.items.length === 0) return;
    return cart.items.map((item) => item.toFormItem())[0];
  }, [cart]);

  const paymentMethodsAvailable = useMemo(() => arePaymentMethodsAvailable(Number(price)).state, [price]);

  //Setting initial cart
  useEffect(() => {
    if (cart && cart.items.length === 0) {
      setCartItem(DefaultCartItem as FormItem, 0);
    }
  }, [cart]);

  //Update in context form valid state
  useEffect(() => {
    updateApplicationContext({ isCartFormValid: isValid });
  }, [isValid]);

  useEffect(() => {
    if (!cart) return;
    const showCartSummary = !!cart.currency;
    const newCartSummaryData = showCartSummary
      ? {
          total: cart.total(),
          totalVat: cart.totalVat() ?? 0,
          currency: cart.currency,
          cartSummaryLines: cart
            .getCompetedItems()
            .map((item) => CartSummaryMap.fromCartItemToCartSummaryLine(item, cart.currency)),
        }
      : undefined;

    updateApplicationContext({
      cartSummaryData: newCartSummaryData,
    });
  }, [cart]);

  // Update cart item when form fields change
  useEffect(() => {
    if (!cartItem || !price) return;
    setCartItem({ ...cartItem, price }, 0);
  }, [price]);

  useEffect(() => {
    if (type === KtuinProductType.OTHERS) {
      resetDescription?.();
      return;
    }
    if (!cartItem || !type) return;
    setCartItem({ ...cartItem, description: type }, 0);
  }, [type]);

  useEffect(() => {
    if (!cartItem || !description) return;
    setCartItem({ ...cartItem, description }, 0);
  }, [description]);

  return {
    cart,
    paymentMethodsAvailable,
    submitCart,
  };
};
